import { useCallback, useEffect, useState } from "react";
import { PackageCheck, X } from "lucide-react";
import DetailDrawer from "../DetailDrawer";
import {
  approveAdminStockRequest,
  getAdminStockRequestById,
  rejectAdminStockRequest,
} from "../../../services/api";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Stock request review drawer.
 *
 * Shows the request and the warehouse stock on hand for each line. Stock is
 * informational only — a request may exceed it and approval is never blocked.
 */

export default function StockRequestDrawer({
  isOpen,
  stockRequestId,
  canReview = false,
  onClose,
  onReviewed,
  t,
  showError,
}) {
  const [request, setRequest] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  // Human-facing reference: the durable order number once loaded, else the raw
  // id as a fallback (e.g. while the detail is still loading).
  const displayRef =
    request?.order_no || (stockRequestId ? `#${stockRequestId}` : "");

  const loadRequest = useCallback(async () => {
    if (!stockRequestId) return;
    try {
      setLoading(true);
      const response = await getAdminStockRequestById(stockRequestId);
      setRequest(response?.data || null);
      setAvailability(
        Array.isArray(response?.availability) ? response.availability : [],
      );
    } catch (error) {
      const message =
        error?.response?.data?.error || t("admin.stockRequests.failedToLoadOne");
      showError(message);
      setRequest(null);
      setAvailability([]);
    } finally {
      setLoading(false);
    }
  }, [stockRequestId, showError, t]);

  useEffect(() => {
    if (!isOpen) {
      setRequest(null);
      setAvailability([]);
      setReviewNotes("");
      setPendingAction(null);
      return;
    }
    loadRequest();
  }, [isOpen, loadRequest]);

  const runAction = async (action) => {
    if (!stockRequestId || submitting) return;

    // Rejecting is the one decision the promoter sees as a refusal, so a reason
    // is required rather than optional.
    if (action === "reject" && !reviewNotes.trim()) {
      showError(t("admin.stockRequests.rejectReasonRequired"));
      return;
    }

    const callers = {
      approve: approveAdminStockRequest,
      reject: rejectAdminStockRequest,
    };
    const successKeys = {
      approve: "admin.stockRequests.approveSuccess",
      reject: "admin.stockRequests.rejectSuccess",
    };

    try {
      setSubmitting(true);
      await callers[action](stockRequestId, {
        review_notes: reviewNotes.trim() || undefined,
      });
      onReviewed?.(
        t(successKeys[action], { id: displayRef }),
      );
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        t("admin.stockRequests.actionFailed");
      showError(message);
      // Re-read: the failure may be a status change made by another admin.
      loadRequest();
    } finally {
      setSubmitting(false);
      setPendingAction(null);
    }
  };

  const items = Array.isArray(request?.items) ? request.items : [];
  const availabilityByProduct = new Map(
    availability.map((row) => [row.product_id, row]),
  );
  const totalUnits = items.reduce(
    (sum, item) => sum + (Number.parseInt(item.quantity, 10) || 0),
    0,
  );

  // Approve / reject are the only decisions; approved is the terminal state.
  // Only reviewers (admin / super_admin / head of promoter) see the actions —
  // OP opens the drawer read-only.
  const isPending = request?.status === "pending" && canReview;

  return (
    <DetailDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={
        displayRef
          ? t("admin.stockRequests.drawerTitle", { id: displayRef })
          : t("admin.stockRequests.drawerTitleFallback")
      }
      subtitle={request?.outlet?.bp_outlet_name || ""}
    >
      {loading ? (
        <p className="admin-stock-drawer-loading">{t("admin.common.loading")}</p>
      ) : !request ? (
        <p className="admin-stock-drawer-loading">
          {t("admin.common.noDataFound")}
        </p>
      ) : (
        <div className="admin-stock-drawer">
          <div className="admin-stock-drawer-status-row">
            <span
              className={`admin-stock-requests-badge is-${request.status}`}
            >
              {t(`admin.stockRequests.status.${request.status}`, {
                defaultValue: request.status,
              })}
            </span>
          </div>

          <dl className="admin-stock-drawer-meta">
            <div>
              <dt>{t("admin.stockRequests.fields.promoter")}</dt>
              <dd>
                {request.promoter?.full_name || "-"}
                {request.promoter?.email ? (
                  <span className="admin-stock-drawer-muted">
                    {" "}
                    ({request.promoter.email})
                  </span>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>{t("admin.stockRequests.fields.outlet")}</dt>
              <dd>{request.outlet?.bp_outlet_name || "-"}</dd>
            </div>
            <div>
              <dt>{t("admin.stockRequests.fields.warehouse")}</dt>
              <dd>
                {request.warehouse
                  ? [
                      request.warehouse.warehouse_code,
                      request.warehouse.warehouse_name,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  : "-"}
              </dd>
            </div>
            <div>
              <dt>{t("admin.stockRequests.fields.requestedAt")}</dt>
              <dd>{formatDateTime(request.requested_at)}</dd>
            </div>
            {request.resubmittedFrom ? (
              <div>
                <dt>
                  {t("admin.stockRequests.fields.resubmittedFrom", {
                    defaultValue: "Resubmitted from",
                  })}
                </dt>
                <dd>
                  {request.resubmittedFrom.order_no ||
                    `#${request.resubmittedFrom.stock_request_id}`}
                </dd>
              </div>
            ) : null}
            {request.reviewed_at ? (
              <div>
                <dt>{t("admin.stockRequests.fields.reviewedAt")}</dt>
                <dd>
                  {formatDateTime(request.reviewed_at)}
                  {request.reviewer?.full_name ? (
                    <span className="admin-stock-drawer-muted">
                      {" "}
                      — {request.reviewer.full_name}
                    </span>
                  ) : null}
                </dd>
              </div>
            ) : null}
          </dl>

          <h4 className="admin-stock-drawer-section-title">
            {t("admin.stockRequests.itemsHeading")}
            {" — "}
            {t("admin.stockRequests.productCount", { count: items.length })}
            {" · "}
            {t("admin.stockRequests.unitCount", { count: totalUnits })}
          </h4>

          <div className="admin-stock-drawer-table-wrap">
            <table className="admin-stock-drawer-table">
              <thead>
                <tr>
                  <th>{t("admin.stockRequests.itemColumns.model")}</th>
                  <th className="is-numeric">
                    {t("admin.stockRequests.itemColumns.requested")}
                  </th>
                  <th className="is-numeric">
                    {t("admin.stockRequests.itemColumns.warehouse")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const avail = availabilityByProduct.get(item.product_id);
                  return (
                    <tr key={item.stock_request_item_id}>
                      <td>
                        <strong>
                          {item.model_name || item.product_name || "-"}
                        </strong>
                        {item.product_name &&
                        item.product_name !== item.model_name ? (
                          <span className="admin-stock-drawer-item-desc">
                            {item.product_name}
                          </span>
                        ) : null}
                      </td>
                      <td className="is-numeric">
                        <strong>{item.quantity}</strong>
                      </td>
                      {/* Warehouse stock on hand — informational only; the
                          request may exceed it. */}
                      <td className="is-numeric">
                        {avail ? avail.available_stock : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {request.notes ? (
            <div className="admin-stock-drawer-notes">
              <h4 className="admin-stock-drawer-section-title">
                {t("admin.stockRequests.fields.promoterNotes")}
              </h4>
              <p>{request.notes}</p>
            </div>
          ) : null}

          {request.review_notes ? (
            <div className="admin-stock-drawer-notes">
              <h4 className="admin-stock-drawer-section-title">
                {t("admin.stockRequests.fields.reviewNotes")}
              </h4>
              <p>{request.review_notes}</p>
            </div>
          ) : null}

          {isPending && (
            <div className="admin-stock-drawer-actions-block">
              <label htmlFor="stockReviewNotes">
                {t("admin.stockRequests.reviewNotesLabel")}
              </label>
              <textarea
                id="stockReviewNotes"
                rows="2"
                value={reviewNotes}
                onChange={(event) => setReviewNotes(event.target.value)}
                placeholder={t("admin.stockRequests.reviewNotesPlaceholder")}
                disabled={submitting}
              />

              {/* Two-step confirm: the decision is visible to the promoter and
                  changes what stock other promoters can see. */}
              {pendingAction ? (
                <div className="admin-stock-drawer-confirm">
                  <p>
                    {t(`admin.stockRequests.confirm.${pendingAction}`, {
                      id: displayRef,
                    })}
                  </p>
                  <div className="admin-stock-drawer-confirm-actions">
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={() => setPendingAction(null)}
                      disabled={submitting}
                    >
                      {t("admin.common.cancel")}
                    </button>
                    <button
                      type="button"
                      className={
                        pendingAction === "reject"
                          ? "admin-btn-danger"
                          : "admin-btn-primary"
                      }
                      onClick={() => runAction(pendingAction)}
                      disabled={submitting}
                    >
                      {submitting
                        ? t("admin.common.processing")
                        : t("admin.stockRequests.confirmYes")}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="admin-stock-drawer-actions">
                  <button
                    type="button"
                    className="admin-btn-danger"
                    onClick={() => setPendingAction("reject")}
                    disabled={submitting}
                  >
                    <X size={16} />
                    {t("admin.common.reject")}
                  </button>
                  <button
                    type="button"
                    className="admin-btn-primary"
                    onClick={() => setPendingAction("approve")}
                    disabled={submitting}
                  >
                    <PackageCheck size={16} />
                    {t("admin.common.approve")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}
